import type { ApiErrorBody } from "@mm/shared";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: { path: (string | number)[]; message: string }[],
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let expiresAt = 0;
let onSignedOut: () => void = () => {};
export const session = {
  set(t: { accessToken: string; accessTokenExpiresAt: string }) {
    accessToken = t.accessToken;
    expiresAt = Date.parse(t.accessTokenExpiresAt);
  },
  clear() {
    accessToken = null;
    expiresAt = 0;
  },
  onSignedOut(cb: () => void) {
    onSignedOut = cb;
  },
  get signedIn() {
    return !!accessToken;
  },
};

/** The refresh token lives in an httpOnly cookie (never readable here); one refresh in flight at a time. */
let refreshing: Promise<boolean> | null = null;
export function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API}/v1/auth/refresh`, { method: "POST", credentials: "include", headers: { "x-client": "web", "content-type": "application/json" }, body: "{}" });
      if (!res.ok) return false;
      session.set(await res.json());
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function toError(res: Response) {
  const b = (await res.json().catch(() => null)) as ApiErrorBody | null;
  return new ApiError(res.status, b?.error.code ?? "unknown", b?.error.message ?? "Something went wrong.", b?.error.fields);
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown; form?: FormData; raw?: boolean } = {}): Promise<T> {
  if (!accessToken || expiresAt - Date.now() < 30_000) await refresh();
  for (let attempt = 0; attempt < 2; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${API}${path}`, {
        method: opts.method ?? "GET",
        credentials: "include",
        headers: {
          "x-client": "web",
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
          ...(opts.body !== undefined ? { "content-type": "application/json" } : {}),
        },
        ...(opts.form ? { body: opts.form } : opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      });
    } catch {
      throw new ApiError(0, "network", "You're offline or the server can't be reached. Try again.");
    }
    if (res.status === 401 && attempt === 0 && (await refresh())) continue;
    if (res.status === 401) {
      session.clear();
      onSignedOut();
    }
    if (!res.ok) throw await toError(res);
    if (res.status === 204) return undefined as T;
    return (opts.raw ? await res.text() : await res.json()) as T;
  }
  throw new ApiError(401, "unauthorized", "Please sign in again.");
}

export async function signIn(email: string, password: string, totp: string) {
  const res = await fetch(`${API}/v1/admin/auth/signin`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", "x-client": "web" },
    body: JSON.stringify({ email, password, totp }),
  });
  if (!res.ok) throw await toError(res);
  session.set(await res.json());
}

export async function signOut() {
  await api("/v1/auth/signout", { method: "POST" }).catch(() => {});
  session.clear();
  onSignedOut();
}

export async function uploadImage(file: File): Promise<{ id: string; fullUrl: string }> {
  const form = new FormData();
  form.append("photo", file, file.name);
  return api("/v1/attachments", { method: "POST", form });
}

export const fieldError = (err: unknown, path: string) =>
  err instanceof ApiError ? (err.fields?.find((f) => f.path.join(".") === path)?.message ?? null) : null;
